import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
    Clock, Wallet, RotateCcw, ClipboardList, MessageSquare, LogIn,
    TrendingUp, TrendingDown, RefreshCw, Building2, Zap,
    PlusCircle, Search, FileText, DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";

const fmtTRY = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtTRY2 = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtInt = (n) => new Intl.NumberFormat("tr-TR").format(Number(n || 0));
const fmtDay = (s) => {
    try { const d = new Date(`${s}T12:00:00`); return `${d.getDate()}/${d.getMonth() + 1}`; } catch { return s; }
};

const CHART_COLORS = ["#8b5cf6", "#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#f43f5e", "#6366f1", "#14b8a6"];

const REFRESH_INTERVAL_MS = 60_000; // 60 sn - anlık güncel için

export default function AdminPage() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const r = await api.get("/admin/dashboard/overview");
            setData(r.data);
            setLastUpdate(new Date());
            setErr(null);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
            if (!silent) toast.error("Dashboard yüklenemedi");
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(false);
        const t = setInterval(() => load(true), REFRESH_INTERVAL_MS);
        return () => clearInterval(t);
    }, [load]);

    if (loading && !data) {
        return (
            <AdminLayout title="Dashboard">
                <div className="flex items-center justify-center h-64 text-slate-500">Yükleniyor…</div>
            </AdminLayout>
        );
    }
    if (err && !data) {
        return (
            <AdminLayout title="Dashboard">
                <div className="p-8 text-center text-rose-600" data-testid="dashboard-error">{err}</div>
            </AdminLayout>
        );
    }

    const actions = data?.actions || {};
    const kpi = data?.kpi || {};
    const topHotels = data?.top_hotels || [];
    const trend = data?.charts?.trend_30d || [];
    const paymentMethods = data?.charts?.payment_methods || [];
    const sources = data?.charts?.sources || [];

    return (
        <AdminLayout title="Dashboard" action={
            <div className="flex items-center gap-2">
                {lastUpdate && (
                    <span className="text-[10px] text-slate-500 font-mono" data-testid="dashboard-last-update">
                        Son güncelleme: {lastUpdate.toLocaleTimeString("tr-TR")}
                    </span>
                )}
                <button onClick={() => load(false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="dashboard-refresh">
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Yenile
                </button>
            </div>
        }>
            {/* KATMAN 1: BUGÜN EYLEME GEÇ */}
            <section className="mb-6" data-testid="section-actions">
                <div className="flex items-center gap-2 mb-3">
                    <Zap className="h-4 w-4 text-amber-500" />
                    <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">Bugün Eyleme Geç</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <ActionCard testid="act-pending" to="/admin/bookings?status=pending" icon={Clock} color="amber"
                        label="Onay Bekleyen" count={actions.pending_bookings?.count} sub={fmtTRY(actions.pending_bookings?.amount)} />
                    <ActionCard testid="act-unpaid" to="/admin/bookings?payment_status=partial" icon={Wallet} color="rose"
                        label="Tahsilat Bekliyor" count={actions.unpaid_confirmed?.count} sub={fmtTRY(actions.unpaid_confirmed?.amount)} />
                    <ActionCard testid="act-refund" to="/admin/refunds" icon={RotateCcw} color="orange"
                        label="İade Bekleyen" count={actions.refund_pending?.count} sub={fmtTRY(actions.refund_pending?.amount)} />
                    <ActionCard testid="act-apps" to="/admin/hotel-applications" icon={ClipboardList} color="violet"
                        label="Yeni Başvuru" count={actions.new_applications?.count} sub="otel başvurusu" />
                    <ActionCard testid="act-reviews" to="/admin/reviews" icon={MessageSquare} color="sky"
                        label="Yorum Onayı" count={actions.pending_reviews?.count} sub="bekliyor" />
                    <ActionCard testid="act-checkin" to={`/admin/bookings?date_field=check_in&date_from=${new Date().toISOString().slice(0, 10)}&date_to=${new Date().toISOString().slice(0, 10)}`} icon={LogIn} color="emerald"
                        label="Bugün Check-in" count={actions.today_checkin?.count} sub={`${actions.today_checkin?.guests || 0} misafir`} />
                </div>
            </section>

            {/* KATMAN 2: KPI KARŞILAŞTIRMA */}
            <section className="mb-6" data-testid="section-kpi">
                <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4 text-violet-500" />
                    <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">Satış & Kâr KPI'ları</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <KpiCard testid="kpi-today" label="Bugün Ciro" value={fmtTRY(kpi.today_revenue)} pct={kpi.today_vs_yesterday_pct} sub={`Dün: ${fmtTRY(kpi.yesterday_revenue)}`} />
                    <KpiCard testid="kpi-month" label="Bu Ay Ciro" value={fmtTRY(kpi.month_revenue)} pct={kpi.month_vs_prev_pct} sub={`Geçen ay: ${fmtTRY(kpi.prev_month_revenue)}`} />
                    <KpiCard testid="kpi-profit" label="Bu Ay Net Kâr" value={fmtTRY(kpi.month_profit)} sub={`Marj: %${kpi.profit_margin_pct}`} />
                    <KpiCard testid="kpi-basket" label="Ort. Sepet" value={fmtTRY2(kpi.avg_basket)} sub="son ay" />
                    <KpiCard testid="kpi-bookings" label="Bu Ay Rez." value={fmtInt(kpi.month_bookings)} sub="rezervasyon" />
                    <KpiCard testid="kpi-cancel" label="İptal Oranı" value={`%${kpi.cancel_rate_pct}`} sub="son 30 gün" reverseColor />
                </div>
            </section>

            {/* KATMAN 3: OTEL PERFORMANS + HIZLI EYLEM */}
            <section className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="section-top-hotels">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-violet-500" /> Otel Performansı — Son 30 Gün
                        </h3>
                        <Link to="/admin/hotels" className="text-[11px] font-semibold text-violet-600 hover:underline">Tüm oteller →</Link>
                    </div>
                    {topHotels.length === 0 ? (
                        <div className="py-8 text-center text-xs text-slate-500">Son 30 günde satış yapan otel yok.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                <tr>
                                    <th className="text-left pb-2">Otel</th>
                                    <th className="text-right pb-2">Ciro</th>
                                    <th className="text-right pb-2">Kâr</th>
                                    <th className="text-right pb-2">Marj</th>
                                    <th className="text-right pb-2">Rez.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topHotels.map((h, i) => (
                                    <tr key={h.hotel_id || i} className="border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer" onClick={() => h.hotel_id && nav(`/admin/hotels/${h.hotel_id}/calendar`)} data-testid={`top-hotel-${i}`}>
                                        <td className="py-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                                                <span className="font-semibold text-slate-800 dark:text-slate-100 truncate max-w-[240px]">{h.hotel_name}</span>
                                            </div>
                                        </td>
                                        <td className="py-2 text-right font-bold text-slate-800 dark:text-slate-100">{fmtTRY(h.revenue)}</td>
                                        <td className="py-2 text-right font-semibold text-emerald-600 dark:text-emerald-400">{fmtTRY(h.profit)}</td>
                                        <td className="py-2 text-right text-xs text-slate-600 dark:text-slate-400">%{h.margin_pct}</td>
                                        <td className="py-2 text-right text-xs text-slate-600 dark:text-slate-400">{h.bookings}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="p-5 rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 text-white" data-testid="section-quick-actions">
                    <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><Zap className="h-4 w-4" /> Hızlı Eylem</h3>
                    <div className="space-y-2">
                        <QuickBtn testid="qa-new-booking" to="/admin/bookings" icon={PlusCircle} label="Rezervasyonlar" />
                        <QuickBtn testid="qa-new-hotel" to="/admin/hotels" icon={Building2} label="Otel Yönetimi" />
                        <QuickBtn testid="qa-finance" to="/admin/finance" icon={DollarSign} label="Finans Merkezi" />
                        <QuickBtn testid="qa-refunds" to="/admin/refunds" icon={RotateCcw} label="İade Bekleyenler" />
                        <QuickBtn testid="qa-reports" to="/admin/finance" icon={FileText} label="Raporlar" />
                        <QuickBtn testid="qa-search" to="/admin/bookings" icon={Search} label="Voucher Ara" />
                    </div>
                </div>
            </section>

            {/* KATMAN 4: GRAFİKLER */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="section-charts">
                {/* 30 günlük ciro & kâr trendi */}
                <div className="lg:col-span-2 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="chart-trend">
                    <h3 className="text-sm font-bold mb-3 text-slate-800 dark:text-slate-100">Son 30 Gün Ciro & Kâr Trendi</h3>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trend} margin={{ top: 5, right: 12, bottom: 5, left: 0 }}>
                                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: "#94a3b8" }} interval={4} />
                                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "none", borderRadius: 8, fontSize: 12 }}
                                    labelStyle={{ color: "#e2e8f0" }}
                                    itemStyle={{ color: "#e2e8f0" }}
                                    labelFormatter={fmtDay}
                                    formatter={(v, name) => [fmtTRY(v), name === "revenue" ? "Ciro" : "Kâr"]}
                                />
                                <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Ciro" />
                                <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2} dot={false} name="Kâr" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Ödeme tipi dağılımı (donut) */}
                <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="chart-payment-methods">
                    <h3 className="text-sm font-bold mb-3 text-slate-800 dark:text-slate-100">Ödeme Tipi Dağılımı</h3>
                    {paymentMethods.length === 0 ? (
                        <div className="h-56 flex items-center justify-center text-xs text-slate-400">Veri yok</div>
                    ) : (
                        <>
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={paymentMethods} dataKey="count" nameKey="label" innerRadius={35} outerRadius={65} paddingAngle={2}>
                                            {paymentMethods.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                                        </Pie>
                                        <Tooltip contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "none", borderRadius: 8, fontSize: 12 }} itemStyle={{ color: "#e2e8f0" }} formatter={(v) => [`${v} adet`, ""]} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="space-y-1 mt-2">
                                {paymentMethods.slice(0, 5).map((p, i) => (
                                    <div key={p.method} className="flex items-center justify-between text-[11px]">
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                            <span className="text-slate-700 dark:text-slate-300">{p.label}</span>
                                        </div>
                                        <span className="font-bold text-slate-800 dark:text-slate-100">{p.count}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Kaynak dağılımı */}
                <div className="lg:col-span-3 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="chart-sources">
                    <h3 className="text-sm font-bold mb-3 text-slate-800 dark:text-slate-100">Rezervasyon Kaynak Dağılımı (Son 30 Gün)</h3>
                    {sources.length === 0 ? (
                        <div className="h-32 flex items-center justify-center text-xs text-slate-400">Veri yok</div>
                    ) : (
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={sources} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                                    <Tooltip contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "none", borderRadius: 8, fontSize: 12 }} itemStyle={{ color: "#e2e8f0" }} formatter={(v) => [fmtTRY(v), "Ciro"]} />
                                    <Bar dataKey="revenue" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </section>
        </AdminLayout>
    );
}

/* ---------- Alt bileşenler ---------- */

function ActionCard({ testid, to, icon: Icon, color, label, count, sub }) {
    const active = Number(count || 0) > 0;
    const palette = {
        amber: { bg: "bg-amber-50 dark:bg-amber-950/30", tx: "text-amber-700 dark:text-amber-300", ring: "ring-amber-200 dark:ring-amber-800/50" },
        rose: { bg: "bg-rose-50 dark:bg-rose-950/30", tx: "text-rose-700 dark:text-rose-300", ring: "ring-rose-200 dark:ring-rose-800/50" },
        orange: { bg: "bg-orange-50 dark:bg-orange-950/30", tx: "text-orange-700 dark:text-orange-300", ring: "ring-orange-200 dark:ring-orange-800/50" },
        violet: { bg: "bg-violet-50 dark:bg-violet-950/30", tx: "text-violet-700 dark:text-violet-300", ring: "ring-violet-200 dark:ring-violet-800/50" },
        sky: { bg: "bg-sky-50 dark:bg-sky-950/30", tx: "text-sky-700 dark:text-sky-300", ring: "ring-sky-200 dark:ring-sky-800/50" },
        emerald: { bg: "bg-emerald-50 dark:bg-emerald-950/30", tx: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-200 dark:ring-emerald-800/50" },
    }[color] || { bg: "bg-slate-50 dark:bg-slate-900", tx: "text-slate-700 dark:text-slate-300", ring: "ring-slate-200 dark:ring-slate-800/50" };
    return (
        <Link
            to={to}
            data-testid={testid}
            className={`p-3 rounded-xl border border-slate-200 dark:border-slate-800 ${active ? palette.bg : "bg-white dark:bg-slate-900"} hover:ring-2 ${palette.ring} transition-all`}
        >
            <div className="flex items-start justify-between gap-2">
                <Icon className={`h-4 w-4 ${palette.tx}`} />
                <span className={`text-2xl font-black leading-none ${active ? palette.tx : "text-slate-400 dark:text-slate-600"}`}>{fmtInt(count)}</span>
            </div>
            <div className="mt-2">
                <div className={`text-[11px] font-bold ${palette.tx}`}>{label}</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{sub}</div>
            </div>
        </Link>
    );
}

function KpiCard({ testid, label, value, pct, sub, reverseColor = false }) {
    const showTrend = pct !== null && pct !== undefined;
    const positive = showTrend && (reverseColor ? pct < 0 : pct > 0);
    const negative = showTrend && (reverseColor ? pct > 0 : pct < 0);
    return (
        <div data-testid={testid} className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
            <div className="mt-1 text-lg font-black text-slate-900 dark:text-white leading-tight">{value}</div>
            <div className="mt-1 flex items-center gap-1 text-[10px]">
                {showTrend && (
                    <span className={`inline-flex items-center gap-0.5 font-bold ${positive ? "text-emerald-600 dark:text-emerald-400" : negative ? "text-rose-600 dark:text-rose-400" : "text-slate-500"}`}>
                        {positive ? <TrendingUp className="h-3 w-3" /> : negative ? <TrendingDown className="h-3 w-3" /> : null}
                        {pct > 0 ? "+" : ""}{pct}%
                    </span>
                )}
                {sub && <span className="text-slate-500 dark:text-slate-400 truncate">{sub}</span>}
            </div>
        </div>
    );
}

function QuickBtn({ testid, to, icon: Icon, label }) {
    return (
        <Link to={to} data-testid={testid} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur transition-colors">
            <Icon className="h-4 w-4" />
            <span className="text-xs font-semibold">{label}</span>
        </Link>
    );
}
